import { HTMLSelect, Icon, InputGroup } from "@blueprintjs/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PaginationButtons } from "@focus/focusUI";
import type { FC, ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CatalogObjectCard } from "../components/catalog/CatalogObjectCard";
import { CatalogOrbitViewer } from "../components/catalog/CatalogOrbitViewer";
import { ConjunctionContactsPanel } from "../components/conjunctions/ConjunctionContactsPanel";
import type { ContactsByOperatorSelection } from "../components/ContactsByOperatorButton";
import { ContentHeader } from "../components/ContentHeader";
import { EmptyStateCard } from "../components/EmptyStateCard";
import { useCatalogue } from "../hooks/useCatalogue";
import { useDebounced } from "../hooks/useDebounced";
import type { CatalogueObject } from "../types/catalog";
import { getDisplayObjectValue } from "../utils/catalogObject";

type CatalogSectionId = "overview" | "objects";
type CatalogFilterField =
  | "name"
  | "noradId"
  | "cosparId"
  | "operator"
  | "status"
  | "regime"
  | "typeOfObject";
type CatalogSortBy = CatalogFilterField;
type SortDirection = "asc" | "desc";

const CATALOG_FIELD_OPTIONS: { label: string; value: CatalogFilterField }[] = [
  { label: "Name", value: "name" },
  { label: "NORAD", value: "noradId" },
  { label: "COSPAR", value: "cosparId" },
  { label: "Organization", value: "operator" },
  { label: "Status", value: "status" },
  { label: "Regime", value: "regime" },
  { label: "Type", value: "typeOfObject" },
];

const SORT_OPTIONS: { label: string; value: CatalogSortBy }[] =
  CATALOG_FIELD_OPTIONS;

const SORT_DIRECTION_OPTIONS: { label: string; value: SortDirection }[] = [
  { label: "Ascending", value: "asc" },
  { label: "Descending", value: "desc" },
];

const FILTER_FIELD_OPTIONS: { label: string; value: CatalogFilterField }[] =
  CATALOG_FIELD_OPTIONS;

const SORT_FIELD_BY_VALUE: Record<CatalogSortBy, string> = {
  name: "name",
  noradId: "noradId",
  cosparId: "cosparId",
  operator: "operator",
  status: "status",
  regime: "regime",
  typeOfObject: "typeOfObject",
};

const catalogSectionTabs: Array<{
  id: CatalogSectionId;
  show: boolean;
  title: string;
}> = [
  { id: "overview", title: "Overview", show: true },
  { id: "objects", title: "Objects", show: true },
];

const scrollIntoSection = (section: HTMLElement) => {
  section.scrollIntoView({ behavior: "smooth", block: "start" });
};

const quoteFilterValue = (value: string) => `"${value.replaceAll('"', '\\"')}"`;

const buildCatalogueFilter = (
  field: CatalogFilterField,
  value: string,
): string | undefined => {
  const query = value.trim();
  if (!query) {
    return undefined;
  }

  if (field === "noradId") {
    return /^\d+$/.test(query) ? `noradId equals ${query}` : undefined;
  }

  if (field === "cosparId") {
    return `cosparId contains ${quoteFilterValue(
      query.replaceAll("-", "").toUpperCase(),
    )}`;
  }

  if (field === "status" || field === "regime" || field === "typeOfObject") {
    return `${field} equals ${quoteFilterValue(query.toUpperCase())}`;
  }

  return `${field} contains ${quoteFilterValue(query)}`;
};

const CatalogSection = ({
  children,
  className = "",
  contentClassName = "flex flex-col gap-4 p-5",
  id,
  title,
}: {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  id: CatalogSectionId;
  title?: string;
}) => (
  <section
    className={`scroll-mt-4 rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}
    id={id}
  >
    {title && (
      <div className="rounded-t-lg border-b border-slate-200 bg-white px-4 py-3">
        <h2 className="m-0 text-lg font-bold text-slate-950">{title}</h2>
      </div>
    )}
    <div className={contentClassName}>{children}</div>
  </section>
);

const SortablePinnedCatalogObjectCard = ({
  object,
  onContactsSelect,
  onTogglePinned,
}: {
  expanded: boolean;
  object: CatalogueObject;
  onContactsSelect: (organization: string, object: CatalogueObject) => void;
  onToggleExpanded: (id: string) => void;
  onTogglePinned: (object: CatalogueObject) => void;
}) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: object.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={isDragging ? "relative z-10 opacity-80" : undefined}
      ref={setNodeRef}
      style={style}
    >
      <CatalogObjectCard
        dragHandle={
          <button
            aria-label={`Reorder ${getDisplayObjectValue(object.name)}`}
            className="inline-flex h-9 w-9 shrink-0 cursor-grab items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 active:cursor-grabbing"
            type="button"
            {...attributes}
            {...listeners}
          >
            <Icon icon="drag-handle-vertical" size={18} />
          </button>
        }
        object={object}
        onContactsSelect={onContactsSelect}
        onTogglePinned={onTogglePinned}
        pinned
      />
    </div>
  );
};

export const CatalogContainer: FC = () => {
  const { catalogue, loadCatalogue } = useCatalogue();

  const [selectedSectionId, setSelectedSectionId] =
    useState<CatalogSectionId>("overview");
  const [filterField, setFilterField] = useState<CatalogFilterField>("name");
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState<CatalogSortBy>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [pinnedObjectsById, setPinnedObjectsById] = useState<
    Record<string, CatalogueObject>
  >({});
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [contactsSelection, setContactsSelection] =
    useState<ContactsByOperatorSelection | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const mainRef = useRef<HTMLElement | null>(null);
  const programmaticScrollSectionRef = useRef<CatalogSectionId | null>(null);
  const pendingScrollSectionRef = useRef<CatalogSectionId | null>(null);
  const sectionRefs = useRef<Record<CatalogSectionId, HTMLElement | null>>({
    overview: null,
    objects: null,
  });
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const { cancel: cancelSearchDebounce, update: updateSearchDebounce } =
    useDebounced<string>({
      initialValue: "",
      onDebounce: setSearchText,
    });

  const tabs = useMemo(
    () =>
      catalogSectionTabs.map((tab) => ({
        ...tab,
        render: () => null,
      })),
    [],
  );

  const pinnedObjects = useMemo(
    () =>
      pinnedIds
        .map((id) => pinnedObjectsById[id])
        .filter((object): object is CatalogueObject => Boolean(object)),
    [pinnedIds, pinnedObjectsById],
  );

  const visibleObjects = useMemo(
    () =>
      (catalogue?.content ?? []).filter(
        (object) => !pinnedIds.includes(object.id),
      ),
    [catalogue, pinnedIds],
  );

  const orbitViewerObjects = useMemo(() => {
    const objectsById = new Map<string, CatalogueObject>();

    for (const object of pinnedObjects) {
      objectsById.set(object.id, object);
    }

    for (const object of catalogue?.content ?? []) {
      objectsById.set(object.id, object);
    }

    return Array.from(objectsById.values());
  }, [catalogue, pinnedObjects]);

  const scrollToSection = useCallback((id: CatalogSectionId) => {
    setSelectedSectionId(id);
    programmaticScrollSectionRef.current = id;
    const section = sectionRefs.current[id];

    if (section) {
      scrollIntoSection(section);
      return;
    }

    pendingScrollSectionRef.current = id;
  }, []);

  const togglePinned = (object: CatalogueObject) => {
    setPinnedIds((current) =>
      current.includes(object.id)
        ? current.filter((pinnedId) => pinnedId !== object.id)
        : [object.id, ...current],
    );
    setPinnedObjectsById((current) => {
      if (object.id in current) {
        const { [object.id]: _removed, ...rest } = current;
        return rest;
      }

      return {
        ...current,
        [object.id]: object,
      };
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) =>
      current.includes(id)
        ? current.filter((expandedId) => expandedId !== id)
        : [...current, id],
    );
  };

  const openOrganizationContacts = (
    organization: string,
    object: CatalogueObject,
  ) => {
    setContactsSelection({
      operator: organization,
      rowId: object.id,
      targetName: getDisplayObjectValue(object.name),
    });
  };

  const handlePinnedDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setPinnedIds((current) => {
      const oldIndex = current.indexOf(String(active.id));
      const newIndex = current.indexOf(String(over.id));

      if (oldIndex === -1 || newIndex === -1) {
        return current;
      }

      return arrayMove(current, oldIndex, newIndex);
    });
  };

  useLayoutEffect(() => {
    const pendingSectionId = pendingScrollSectionRef.current;
    if (!pendingSectionId) return;

    const section = sectionRefs.current[pendingSectionId];
    if (!section) return;

    section.scrollIntoView({ behavior: "auto", block: "start" });
    pendingScrollSectionRef.current = null;
    programmaticScrollSectionRef.current = null;
  }, [selectedSectionId]);

  useEffect(() => {
    const container = mainRef.current;
    if (!container) return;

    const handleScroll = () => {
      const bottomOffset =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      const programmaticSectionId = programmaticScrollSectionRef.current;

      if (programmaticSectionId) {
        const targetSection = sectionRefs.current[programmaticSectionId];
        if (!targetSection) return;

        const containerTop = container.getBoundingClientRect().top;
        const offsetFromTop =
          targetSection.getBoundingClientRect().top - containerTop;

        if (Math.abs(offsetFromTop) <= 4 || bottomOffset <= 2) {
          programmaticScrollSectionRef.current = null;
        } else {
          setSelectedSectionId(programmaticSectionId);
          return;
        }
      }

      if (bottomOffset <= 2) {
        setSelectedSectionId(
          catalogSectionTabs[catalogSectionTabs.length - 1].id,
        );
        return;
      }

      const containerTop = container.getBoundingClientRect().top;
      const threshold = 80;
      const activeId = catalogSectionTabs.reduce<CatalogSectionId>(
        (current, tab) => {
          const section = sectionRefs.current[tab.id];
          if (!section) return current;

          const offsetFromTop =
            section.getBoundingClientRect().top - containerTop;

          return offsetFromTop <= threshold ? tab.id : current;
        },
        catalogSectionTabs[0].id,
      );

      setSelectedSectionId(activeId);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => cancelSearchDebounce, [cancelSearchDebounce]);

  useEffect(() => {
    if (!catalogue?.content.length || !pinnedIds.length) {
      return;
    }

    setPinnedObjectsById((current) => {
      let changed = false;
      const next = { ...current };

      for (const object of catalogue.content) {
        if (!pinnedIds.includes(object.id)) {
          continue;
        }

        if (next[object.id] !== object) {
          next[object.id] = object;
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [catalogue, pinnedIds]);

  useEffect(() => {
    loadCatalogue({
      page: pageIndex,
      pageSize,
      filter: buildCatalogueFilter(filterField, searchText),
      sort: `${SORT_FIELD_BY_VALUE[sortBy]}:${sortDirection}`,
    });
  }, [
    filterField,
    loadCatalogue,
    pageIndex,
    pageSize,
    searchText,
    sortBy,
    sortDirection,
  ]);

  if (!catalogue) return null;

  return (
    <div className="foc-content flex flex-col overflow-hidden">
      <ContentHeader
        tabsAlign="start"
        tabs={tabs}
        selectedTab={selectedSectionId}
        handleSelectTab={(id) => scrollToSection(id as CatalogSectionId)}
      />

      <main className="flex min-h-0 flex-1 overflow-hidden bg-slate-50/40">
        {contactsSelection && (
          <div className="shrink-0 p-4 pr-0">
            <ConjunctionContactsPanel
              className="h-full"
              onClose={() => setContactsSelection(null)}
              selection={contactsSelection}
              variant="card"
            />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-4 !pt-0" ref={mainRef}>
          <div className="flex w-full flex-col gap-4 pb-4">
            <div
              ref={(node) => {
                sectionRefs.current.overview = node;
              }}
            >
              <CatalogOrbitViewer
                className="flex min-h-0 h-[calc(100vh-170px)] flex-1 flex-col"
                objects={orbitViewerObjects}
                viewerClassName="min-h-0 flex-1"
              />
            </div>

            <div
              ref={(node) => {
                sectionRefs.current.objects = node;
              }}
            >
              <CatalogSection
                id="objects"
                contentClassName="flex flex-col p-0"
                title="Objects"
              >
                <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
                  <div className="focusoc-form-shell flex flex-wrap items-end gap-3">
                    <label className="flex min-w-[220px] flex-[1.4] flex-col gap-1.5">
                      <span className="text-xs font-semibold uppercase text-slate-500">
                        Search
                      </span>
                      <InputGroup
                        aria-label="Catalogue search value"
                        leftIcon="search"
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setPageIndex(0);
                          setSearchInput(nextValue);
                          updateSearchDebounce(() => nextValue);
                        }}
                        placeholder="Enter a value for the selected field"
                        type="search"
                        value={searchInput}
                      />
                    </label>

                    <label className="flex min-w-[180px] flex-col gap-1.5">
                      <span className="text-xs font-semibold uppercase text-slate-500">
                        Filter by
                      </span>
                      <HTMLSelect
                        aria-label="Filter catalogue by field"
                        fill
                        onChange={(event) => {
                          setPageIndex(0);
                          setFilterField(
                            event.currentTarget.value as CatalogFilterField,
                          );
                        }}
                        options={FILTER_FIELD_OPTIONS}
                        value={filterField}
                      />
                    </label>

                    <label className="flex min-w-[180px] flex-col gap-1.5">
                      <span className="text-xs font-semibold uppercase text-slate-500">
                        Sort by
                      </span>
                      <HTMLSelect
                        aria-label="Sort catalogue by field"
                        fill
                        onChange={(event) => {
                          setPageIndex(0);
                          setSortBy(event.currentTarget.value as CatalogSortBy);
                        }}
                        options={SORT_OPTIONS}
                        value={sortBy}
                      />
                    </label>

                    <label className="flex min-w-[150px] flex-col gap-1.5">
                      <span className="text-xs font-semibold uppercase text-slate-500">
                        Order
                      </span>
                      <HTMLSelect
                        aria-label="Sort catalogue order"
                        fill
                        onChange={(event) => {
                          setPageIndex(0);
                          setSortDirection(
                            event.currentTarget.value as SortDirection,
                          );
                        }}
                        options={SORT_DIRECTION_OPTIONS}
                        value={sortDirection}
                      />
                    </label>

                    <span className="mb-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
                      {visibleObjects.length} / {catalogue.totalElements}
                    </span>
                  </div>
                </div>

                {pinnedObjects.length > 0 && (
                  <section className="shrink-0 border-b border-slate-200 bg-slate-50/70 px-4 py-3 translate-y-0 opacity-100 motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out starting:-translate-y-2 starting:opacity-0">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-bold uppercase text-slate-500">
                        <Icon icon="pin" size={14} />
                        Pinned objects
                      </div>
                      <span className="text-xs font-semibold uppercase text-slate-500">
                        {pinnedObjects.length} pinned
                      </span>
                    </div>
                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handlePinnedDragEnd}
                      sensors={sensors}
                    >
                      <SortableContext
                        items={pinnedIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="flex flex-col gap-3">
                          {pinnedObjects.map((object) => (
                            <SortablePinnedCatalogObjectCard
                              expanded={expandedIds.includes(object.id)}
                              key={object.id}
                              object={object}
                              onContactsSelect={openOrganizationContacts}
                              onToggleExpanded={toggleExpanded}
                              onTogglePinned={togglePinned}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </section>
                )}

                <section className="flex flex-col">
                  <div className="mb-2 flex shrink-0 items-center justify-between gap-3 px-4 pt-4">
                    <h2 className="m-0 text-base font-bold text-slate-950">
                      Search results
                    </h2>
                    <span className="text-sm font-semibold text-slate-500">
                      {visibleObjects.length} objects
                    </span>
                  </div>

                  <div
                    className={`overflow-y-auto px-4 pr-1 ${
                      visibleObjects.length > 0
                        ? "h-[calc(100vh-460px)] min-h-[260px]"
                        : "min-h-[220px]"
                    }`}
                  >
                    {visibleObjects.length > 0 ? (
                      <div className="flex flex-col gap-3">
                        {visibleObjects.map((object) => (
                          <CatalogObjectCard
                            key={object.id}
                            object={object}
                            onContactsSelect={openOrganizationContacts}
                            onTogglePinned={togglePinned}
                            pinned={false}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyStateCard
                        icon="satellite"
                        title="No objects match the current filters"
                        description="Adjust search or sort controls to widen the list."
                      />
                    )}
                  </div>

                  <div className="focusoc-table-pagination rounded-b-2xl overflow-hidden">
                    <PaginationButtons
                      pageIndex={pageIndex}
                      pageCount={catalogue.totalPages}
                      canPreviousPage={!catalogue.first}
                      canNextPage={!catalogue.last}
                      rowCountOnCurrentPage={catalogue.content.length}
                      pageSize={pageSize}
                      onFirstPage={() => setPageIndex(0)}
                      onPreviousPage={() =>
                        setPageIndex((current) => Math.max(0, current - 1))
                      }
                      onNextPage={() =>
                        setPageIndex((current) =>
                          Math.min(
                            (catalogue.totalPages ?? 1) - 1,
                            current + 1,
                          ),
                        )
                      }
                      onLastPage={() =>
                        setPageIndex(
                          Math.max((catalogue.totalPages ?? 1) - 1, 0),
                        )
                      }
                      onPageIndexChange={setPageIndex}
                      onPageSizeChange={(nextPageSize) => {
                        setPageSize(nextPageSize);
                        setPageIndex(0);
                      }}
                      paginationOptions={{
                        disableGoToPage: false,
                        disableRowsPerPage: false,
                      }}
                    />
                  </div>
                </section>
              </CatalogSection>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
